(() => {
  let viewerScript;
  class AetheriaBookOne extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.events = new AbortController();
      this.on = (node, type, handler, options = {}) => node.addEventListener(type, handler, { ...options, signal: this.events.signal });
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.form = this.querySelector('[data-order-form]');
      this.first = this.querySelector('[data-book-one]');
      this.addons = [...this.querySelectorAll('[data-addon]')];
      this.addButtons = [...this.querySelectorAll('[data-add]')];
      this.seriesButtons = [...this.querySelectorAll('[data-series]')];
      this.selectionBar = this.querySelector('[data-selection-bar]');
      this.heroPurchase = this.querySelector('[data-hero-purchase]');
      this.finalPurchase = this.querySelector('[data-final-purchase]');
      this.story = this.querySelector('[data-story]');
      this.status = this.querySelector('[data-status]');
      this.cartLink = this.querySelector('[data-cart-link]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: this.dataset.currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
      });
      this.on(this.form, 'submit', (event) => { event.preventDefault(); this.addToCart(this.selected()); });
      this.addons.forEach((input) => this.on(input, 'change', () => this.render()));
      this.seriesButtons.forEach((button) => this.on(button, 'click', () => {
        if (this.blocked() || button.dataset.available !== 'true') return;
        this.addons.forEach((input) => { input.checked = true; });
        this.render();
        this.addToCart([this.first, ...this.addons]);
      }));
      this.render();
      this.setupOffer();
      this.setupDetails();
      this.setupModel();
      const scroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.syncBar();
          if (this.story.getBoundingClientRect().bottom <= 0) this.autoOffer();
        });
      };
      this.on(window, 'scroll', scroll, { passive: true });
      this.on(window, 'resize', scroll);
      this.on(this.mobile, 'change', () => this.syncBar());
      this.on(this.motion, 'change', () => { this.syncModelMotion(); this.syncDetails(); });
      this.on(document, 'visibilitychange', () => { this.syncDetails(); this.syncModelMotion(); });
      this.visibilityObserver = new IntersectionObserver(() => this.syncBar());
      this.visibilityObserver.observe(this.heroPurchase);
      this.visibilityObserver.observe(this.finalPurchase);
      const loaded = () => {
        this.loaded = true;
        let interacted = false;
        const interaction = () => {
          if (interacted) return;
          interacted = true;
          const idle = () => { this.modelIdle = true; this.startModel(); };
          if ('requestIdleCallback' in window) this.idleId = requestIdleCallback(idle, { timeout: 1500 });
          else this.idleTimer = setTimeout(idle, 1500);
        };
        ['pointerdown', 'pointermove', 'touchstart', 'wheel', 'keydown', 'scroll'].forEach((type) => this.on(window, type, interaction, { passive: true, once: true }));
        this.offerTimer = setTimeout(() => this.autoOffer(), 20000);
        if (this.offerRequested) this.openOffer();
        else if (this.story.getBoundingClientRect().bottom <= 0) this.autoOffer();
      };
      if (document.readyState === 'complete') loaded();
      else this.on(window, 'load', loaded, { once: true });
      this.syncBar();
    }
    disconnectedCallback() {
      this.events?.abort();
      [this.visibilityObserver, this.modelObserver, this.detailObserver].forEach((observer) => observer?.disconnect());
      [this.idleTimer, this.offerTimer, this.modelTimeout].forEach(clearTimeout);
      clearInterval(this.detailTimer);
      if (this.idleId != null) window.cancelIdleCallback?.(this.idleId);
      cancelAnimationFrame(this.scrollFrame);
      this.subscriptionController?.abort();
      this.initialized = false;
    }
    blocked() { return this.pending || this.uncertain || this.confirmed; }
    selected() { return [this.first, ...this.addons.filter((input) => input.checked && input.dataset.available === 'true')]; }
    total(inputs) { return inputs.reduce((sum, input) => sum + Number(input.dataset.price), 0); }
    render() {
      const selected = this.selected();
      const total = this.total(selected);
      const amount = this.money.format(total / 100);
      const count = selected.length;
      this.querySelector('[data-total]').textContent = amount;
      this.querySelector('[data-shipping]').textContent = total >= 10000 ? this.copy.shipping_free : this.copy.shipping_nudge;
      this.addButtons.forEach((button) => {
        button.textContent = this.pending ? this.copy.adding : `${this.copy.add} · ${count} ${count === 1 ? this.copy.book : this.copy.books} · ${amount}`;
        button.disabled = this.blocked() || this.first.dataset.available !== 'true';
      });
      this.seriesButtons.forEach((button) => {
        button.textContent = this.pending ? this.copy.adding : this.copy.series.replace('[total]', this.money.format(this.total([this.first, ...this.addons]) / 100));
        button.disabled = this.blocked() || button.dataset.available !== 'true';
      });
      this.addons.forEach((input) => { input.disabled = this.blocked() || input.dataset.available !== 'true'; });
      this.form.setAttribute('aria-busy', String(Boolean(this.pending)));
    }
    syncBar() {
      const hero = this.heroPurchase.getBoundingClientRect();
      const final = this.finalPurchase.getBoundingClientRect();
      const show = this.mobile.matches && hero.bottom <= 0 && final.top >= innerHeight && !this.dialog?.open;
      this.selectionBar.hidden = !show;
      this.selectionBar.inert = !show;
      this.selectionBar.setAttribute('aria-hidden', String(!show));
    }
    setupModel() {
      this.modelStage = this.querySelector('[data-model-stage]');
      this.modelObserver = new IntersectionObserver((entries) => {
        this.modelVisible = entries[0].isIntersecting;
        this.startModel();
      });
      this.modelObserver.observe(this.modelStage);
    }
    async startModel() {
      if (!this.loaded || !this.modelIdle || !this.modelVisible || this.modelStarted || navigator.connection?.saveData) return;
      this.modelStarted = true;
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
        if (!gl) return;
        gl.getExtension('WEBGL_lose_context')?.loseContext();
        if (!customElements.get('model-viewer')) {
          if (!viewerScript) viewerScript = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.type = 'module';
            script.src = this.modelStage.dataset.viewerUrl;
            script.onload = resolve;
            script.onerror = reject;
            document.head.append(script);
          });
          await viewerScript;
        }
        if (!customElements.get('model-viewer') || !this.isConnected) return;
        const viewer = document.createElement('model-viewer');
        this.viewer = viewer;
        const attributes = {
          src: this.modelStage.dataset.modelUrl, poster: this.modelStage.querySelector('img').src,
          alt: 'Crown of Light and Shadows', 'aria-label': 'Rotate Crown of Light and Shadows',
          'camera-controls': '', 'disable-pan': '', 'disable-zoom': '', 'disable-tap': '',
          'interaction-prompt': 'none', 'touch-action': 'pan-y', 'camera-orbit': '122deg 80deg 100%',
          'min-camera-orbit': 'auto 10deg 100%', 'max-camera-orbit': 'auto 170deg 100%',
          'camera-target': 'auto auto auto', 'field-of-view': '30deg', 'rotation-per-second': '10deg',
          'auto-rotate-delay': '400', 'environment-image': this.modelStage.dataset.environmentUrl,
          'tone-mapping': 'neutral', exposure: '1', 'shadow-intensity': '0',
        };
        Object.entries(attributes).forEach(([name, value]) => viewer.setAttribute(name, value));
        viewer.setAttribute('aria-hidden', 'true');
        viewer.inert = true;
        const failed = () => {
          clearTimeout(this.modelTimeout);
          this.modelStage.classList.remove('is-ready');
          this.querySelector('[data-model-hint]').setAttribute('aria-hidden', 'true');
          viewer.remove();
        };
        this.on(viewer, 'load', () => {
          clearTimeout(this.modelTimeout);
          this.modelStage.classList.add('is-ready');
          viewer.removeAttribute('aria-hidden');
          viewer.inert = false;
          this.querySelector('[data-model-hint]').setAttribute('aria-hidden', 'false');
        }, { once: true });
        this.on(viewer, 'error', failed);
        this.on(viewer, 'camera-change', (event) => {
          if (event.detail?.source === 'user-interaction') { this.modelTurned = true; this.syncModelMotion(); }
        });
        this.syncModelMotion();
        this.modelStage.append(viewer);
        this.modelTimeout = setTimeout(failed, 30000);
      } catch { /* The eager still remains the usable fallback. */ }
    }
    syncModelMotion() {
      this.viewer?.toggleAttribute('auto-rotate', !this.motion.matches && !this.modelTurned && !document.hidden);
    }
    setupDetails() {
      this.detailRegion = this.querySelector('[data-details]');
      this.detailTabs = [...this.querySelectorAll('[data-detail-tab]')];
      this.detailSlides = [...this.querySelectorAll('[data-detail-slide]')];
      this.detailIndex = 0;
      this.detailTabs.forEach((tab, index) => this.on(tab, 'click', () => {
        this.detailChosen = true;
        this.showDetail(index);
        this.syncDetails();
      }));
      this.on(this.detailRegion, 'pointerenter', () => { this.detailHovered = true; this.syncDetails(); });
      this.on(this.detailRegion, 'pointerleave', () => { this.detailHovered = false; this.syncDetails(); });
      this.on(this.detailRegion, 'focusin', () => { this.detailFocused = true; this.syncDetails(); });
      this.on(this.detailRegion, 'focusout', (event) => {
        this.detailFocused = this.detailRegion.contains(event.relatedTarget);
        this.syncDetails();
      });
      this.detailObserver = new IntersectionObserver((entries) => {
        this.detailVisible = entries[0].isIntersecting;
        this.syncDetails();
      }, { threshold: 0.35 });
      this.detailObserver.observe(this.detailRegion);
      this.showDetail(0);
    }
    showDetail(index) {
      this.detailIndex = index;
      this.detailTabs.forEach((tab, i) => tab.setAttribute('aria-pressed', String(i === index)));
      this.detailSlides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.setAttribute('aria-hidden', String(i !== index));
      });
    }
    syncDetails() {
      const run = !this.motion.matches && !document.hidden && this.detailVisible && !this.detailHovered && !this.detailFocused && !this.detailChosen && !this.dialog?.open;
      this.detailRegion.classList.toggle('is-cycling', Boolean(run));
      if (run && !this.detailTimer) this.detailTimer = setInterval(() => this.showDetail((this.detailIndex + 1) % this.detailTabs.length), Number(this.detailRegion.dataset.interval) || 4500);
      else if (!run && this.detailTimer) { clearInterval(this.detailTimer); this.detailTimer = null; }
    }
    stored(key, value) {
      try {
        if (value !== undefined) localStorage.setItem(key, value);
        return localStorage.getItem(key);
      } catch { return null; }
    }
    setupOffer() {
      this.dialog = this.querySelector('[data-offer]');
      this.offerForm = this.querySelector('[data-offer-form]');
      this.offerEmail = this.querySelector('[data-offer-email]');
      this.offerSubmit = this.querySelector('[data-offer-submit]');
      this.offerError = this.querySelector('[data-offer-error]');
      this.offerSuccess = this.querySelector('[data-offer-success]');
      this.subscribed = this.stored('mrp-coa-offer-subscribed') === 'true';
      this.on(this.querySelector('[data-offer-open]'), 'click', () => this.openOffer());
      this.on(this.querySelector('[data-offer-close]'), 'click', () => this.closeOffer());
      this.on(this.dialog, 'click', (event) => {
        const box = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) this.closeOffer();
      });
      this.on(this.dialog, 'cancel', (event) => { event.preventDefault(); this.closeOffer(); });
      this.on(this.dialog, 'close', () => {
        this.stored('mrp-coa-offer-dismissed', String(Date.now()));
        this.syncBar();
        this.syncDetails();
        this.offerReturnFocus?.focus({ preventScroll: true });
      });
      this.on(this.dialog, 'keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('button, input, a[href]')].filter((node) => !node.disabled && node.getClientRects().length);
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      });
      this.on(this.offerForm, 'submit', (event) => { event.preventDefault(); this.subscribe(); });
      this.on(this.offerEmail, 'input', () => { this.offerEmail.removeAttribute('aria-invalid'); this.offerError.textContent = ''; });
      this.on(this.querySelector('[data-copy-code]'), 'click', async () => {
        const status = this.querySelector('[data-copy-status]');
        try { await navigator.clipboard.writeText(this.copy.offer_code); status.textContent = this.copy.offer_copied; }
        catch { status.textContent = this.copy.offer_copy_error; }
      });
    }
    closeOffer() {
      this.stored('mrp-coa-offer-dismissed', String(Date.now()));
      this.dialog.close();
      this.syncBar();
      this.syncDetails();
      this.offerReturnFocus?.focus({ preventScroll: true });
    }
    autoOffer() {
      const dismissed = Number(this.stored('mrp-coa-offer-dismissed'));
      if (!this.loaded || this.autoShown || this.subscribed || document.hidden || this.blocked() || (dismissed > 0 && Date.now() - dismissed < 7 * 24 * 60 * 60 * 1000)) return;
      this.openOffer();
    }
    openOffer() {
      if (!this.loaded) { this.offerRequested = true; return; }
      if (this.dialog.open) return;
      this.autoShown = true;
      clearTimeout(this.offerTimer);
      this.offerReturnFocus = document.activeElement;
      this.subscribed ||= this.stored('mrp-coa-offer-subscribed') === 'true';
      this.offerForm.hidden = this.subscribed;
      this.offerSuccess.hidden = !this.subscribed;
      this.dialog.showModal();
      if (this.subscribed) this.offerSuccess.querySelector('[role="status"]').focus();
      else this.offerEmail.focus();
      this.syncBar();
      this.syncDetails();
    }
    async subscribe() {
      if (this.subscribing || this.subscribed) return;
      const email = this.offerEmail.value.trim();
      if (!this.offerEmail.validity.valid || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.offerError.textContent = this.copy.offer_invalid;
        this.offerEmail.setAttribute('aria-invalid', 'true');
        this.offerEmail.focus();
        return;
      }
      this.subscribing = true;
      this.offerSubmit.disabled = true;
      this.offerForm.setAttribute('aria-busy', 'true');
      this.offerError.textContent = '';
      this.subscriptionController = new AbortController();
      const timeout = setTimeout(() => this.subscriptionController.abort(), 15000);
      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.copy.offer_company)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
          body: JSON.stringify({ data: {
            type: 'subscription',
            attributes: { custom_source: this.copy.offer_source, profile: { data: { type: 'profile', attributes: {
              email, properties: { coa_series_offer: true, coa_series_offer_source: this.copy.offer_source },
            } } } },
            relationships: { list: { data: { type: 'list', id: this.copy.offer_list } } },
          } }), signal: this.subscriptionController.signal,
        });
        if (!response.ok) throw new Error('Subscription rejected');
        this.subscribed = true;
        this.stored('mrp-coa-offer-subscribed', 'true');
        this.offerForm.hidden = true;
        this.offerSuccess.hidden = false;
        if (this.dialog.open) this.offerSuccess.querySelector('[role="status"]').focus();
      } catch { this.offerError.textContent = this.copy.offer_error; }
      finally {
        clearTimeout(timeout);
        this.subscribing = false;
        this.offerSubmit.disabled = false;
        this.offerForm.setAttribute('aria-busy', 'false');
      }
    }
    async addToCart(inputs) {
      if (this.blocked() || inputs.some((input) => input.dataset.available !== 'true')) return;
      const items = inputs.map((input) => ({ id: Number(input.value), quantity: 1 }));
      const root = window.Shopify?.routes?.root || this.dataset.cartRoot || '/';
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      this.pending = true;
      this.status.textContent = this.copy.adding;
      this.cartLink.hidden = true;
      this.render();
      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }), signal: controller.signal,
        });
        if (!response.ok) {
          if (response.status >= 500) throw new Error('Unconfirmed cart');
          this.status.textContent = this.copy.stock_error;
          this.cartLink.hidden = false;
          return;
        }
        const result = await response.json();
        if (!Array.isArray(result.items) || result.items.length !== items.length ||
          items.some((item) => !result.items.some((added) => Number(added.variant_id ?? added.id) === item.id))) {
          throw new Error('Unconfirmed cart');
        }
        this.confirmed = true;
        this.status.textContent = this.copy.added;
        this.cartLink.hidden = false;
        window.location.assign(root + 'cart');
      } catch {
        this.uncertain = true;
        this.status.textContent = this.copy.network_error;
        this.cartLink.hidden = false;
      } finally {
        clearTimeout(timeout);
        this.pending = false;
        this.render();
        if (!this.confirmed) {
          this.status.setAttribute('tabindex', '-1');
          this.status.focus();
        }
      }
    }
  }
  if (!customElements.get('mrp-aetheria-book-one')) customElements.define('mrp-aetheria-book-one', AetheriaBookOne);
})();
